import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import ABHACreationFlow from './ABHACreationFlow';
import ABHAVerificationFlow from './ABHAVerificationFlow';
import ScanShareCounterCards from './counters/ScanShareCounterCards';
import LinkedRecordsList from './LinkedRecordsList';
import SendAbhaLinkSms from '../hip/SendAbhaLinkSms';
import { toABHAProfileSnapshot, maskAbhaNumber, maskAbhaAddress } from '../../services/api/abhaProfile';
import { getABHARecord } from '../../services/api/abhaApi';
import { abhaStatusMeta, abhaToneColors } from '../visit/abhaStatusMeta';
import { usePermission, useAnyPermission } from '../../context/PermissionContext';
import { ABDM_ANY_ABHA } from '../../constants/abdmPermissions';
import { ABHA_SECTION, ABHA_NUMBER_LABEL, ABHA_ADDRESS_LABEL, VERIFY_ABHA, CREATE_ABHA } from '../../constants/abdmCopy';

/**
 * ABHASection — collapsible integration wrapper for the registration form.
 *
 * Manages all ABHA workflow state internally (mode, QR toggle).
 * Emits only normalized ABHAProfileSnapshot objects upward — the parent
 * never sees txnId, OTP steps, retry counters, or WS state.
 *
 * Props:
 *   abhaResult     — ABHAProfileSnapshot | null (controlled by parent)
 *   onAbhaComplete(snapshot | null)  — parent sets/clears abhaResult
 *   patientId      — string | null   — patient_id of the selected patient
 *   abhaStatus     — 'NOT_LINKED' | 'LINKED' | 'VERIFIED' (from patient record)
 *   initialMode    — 'create' | 'verify' | undefined — when set, the section opens directly in
 *                    that flow (skipping the chooser AND the record lazy-fetch), so a caller can
 *                    express intent (e.g. a "Verify ABHA" button). Default → today's chooser.
 *   defaultOpen    — start expanded (e.g. when hosted in a modal). Default false (collapsed).
 *   hideHeader     — hide the internal collapsible header (for modal hosting where the host owns
 *                    the title/close). Default false. Implies the body is always shown.
 */
export default function ABHASection({ abhaResult, onAbhaComplete, patientId, abhaStatus, patientName = '', initialMode, defaultOpen = false, hideHeader = false, defaultMobile = '' }) {
  const { isDark } = useTheme();
  const canCreate = usePermission('ABHA_CREATE');
  const canVerify = usePermission('ABHA_VERIFY');
  const canScanShare = usePermission('ABHA_SCAN_SHARE');
  const canAny = useAnyPermission(...ABDM_ANY_ABHA);
  const [isOpen, setIsOpen] = useState(Boolean(initialMode) || defaultOpen);
  const [linkedShown, setLinkedShown] = useState(false); // R130: full linked box replaces the masked lines
  const [mode, setMode] = useState(initialMode || 'idle'); // 'idle' | 'create' | 'verify'
  const [showQR, setShowQR] = useState(false);
  const [recordDegraded, setRecordDegraded] = useState(false);
  const fetchCacheKey = useRef(null);

  // Auto-expand when a result arrives (e.g. scan-share opened the section)
  useEffect(() => {
    if (abhaResult) setIsOpen(true);
  }, [abhaResult]);

  // Lazy-fetch ABHA record for returning patients (abhaStatus !== 'NOT_LINKED').
  // Cached by (patientId:abhaStatus) — clears automatically on patient change or status change.
  useEffect(() => {
    if (!canAny) return; // no ABHA slug — the section is not shown
    if (initialMode) return; // caller forced a specific flow — don't preempt it with the record
    if (!patientId || !abhaStatus || abhaStatus === 'NOT_LINKED') return;
    if (abhaResult) return; // already populated — don't overwrite an active flow result

    const cacheKey = `${patientId}:${abhaStatus}`;
    if (fetchCacheKey.current === cacheKey) return; // already fetched for this patient+status

    let cancelled = false;
    fetchCacheKey.current = cacheKey;
    setRecordDegraded(false);

    getABHARecord({ patientId })
      .then((rec) => {
        if (cancelled) return;
        if (rec.detailsAvailable) {
          const snapshot = toABHAProfileSnapshot(
            { abhaNumber: rec.abhaNumberMasked, abhaAddress: rec.abhaAddress, verifiedAt: rec.verifiedAt },
            'repository',
          );
          onAbhaComplete?.(snapshot);
          setIsOpen(true);
        } else {
          // Record exists in patient table but ABHA details are missing — surface degraded badge.
          setRecordDegraded(true);
          setIsOpen(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRecordDegraded(true);
          setIsOpen(true);
        }
      });

    return () => { cancelled = true; };
  }, [patientId, abhaStatus, abhaResult, onAbhaComplete, initialMode, canAny]);

  const handleCreationComplete = useCallback((raw) => {
    setMode('idle');
    onAbhaComplete?.(toABHAProfileSnapshot(raw, 'creation'));
  }, [onAbhaComplete]);

  const handleVerificationComplete = useCallback((raw) => {
    setMode('idle');
    onAbhaComplete?.(toABHAProfileSnapshot(raw, 'verification'));
  }, [onAbhaComplete]);

  const handleSkip = useCallback(() => setMode('idle'), []);
  const handleClear = useCallback(() => {
    onAbhaComplete?.(null);
    setMode('idle');
    setShowQR(false);
    setRecordDegraded(false);
    fetchCacheKey.current = null; // allow re-fetch if patient re-selected
  }, [onAbhaComplete]);

  // ── Styles ────────────────────────────────────────────────────────────

  const sectionWrap = {
    borderRadius: 10,
    border: `1px solid ${isDark ? 'rgba(20,184,166,0.2)' : 'rgba(20,184,166,0.25)'}`,
    overflow: 'hidden',
  };

  const headerBtn = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    background: isDark ? 'rgba(20,184,166,0.07)' : '#f0fdfa',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    gap: 8,
  };

  const chevron = {
    fontSize: 11,
    color: isDark ? '#2dd4bf' : '#0f766e',
    flexShrink: 0,
    transition: 'transform 0.2s',
    transform: isOpen ? 'rotate(180deg)' : 'none',
  };

  const body = {
    padding: '12px 14px 14px',
    background: isDark ? '#0f172a' : '#fff',
    borderTop: `1px solid ${isDark ? 'rgba(20,184,166,0.12)' : 'rgba(20,184,166,0.15)'}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  };

  // Status of what is shown: a number loaded from file ('repository') keeps the patient's own
  // status (LINKED = on file, NOT proven); any in-session result (OTP / create / scan) is verified.
  const resultMeta = abhaStatusMeta(abhaResult && abhaResult.source !== 'repository' ? 'VERIFIED' : abhaStatus);

  // ── Degraded state (status=LINKED/VERIFIED but details unavailable) ──

  const degradedBody = (
    <div style={body}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px', borderRadius: 8,
        background: isDark ? 'rgba(120,53,15,0.3)' : '#fffbeb',
        border: `1px solid ${isDark ? '#92400e' : '#fcd34d'}`,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: isDark ? '#fcd34d' : '#92400e' }}>
            {ABHA_SECTION.onFile}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 11, color: isDark ? '#d97706' : '#b45309' }}>
            {ABHA_SECTION.degraded}
          </p>
        </div>
        <button
          type="button"
          onClick={handleClear}
          style={{
            flexShrink: 0, fontSize: 12, fontWeight: 600, padding: '4px 10px',
            borderRadius: 6, cursor: 'pointer',
            border: `1px solid ${isDark ? '#334155' : '#cbd5e1'}`,
            background: isDark ? '#1e293b' : '#f8fafc',
            color: isDark ? '#94a3b8' : '#64748b',
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );

  // ── Completed state ───────────────────────────────────────────────────

  const completedBody = (
    <div style={body}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px', borderRadius: 8,
        background: isDark ? 'rgba(6,78,59,0.3)' : '#ecfdf5',
        border: `1px solid ${isDark ? '#065f46' : '#6ee7b7'}`,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span data-testid="abha-section-status" style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 12, fontWeight: 700, color: abhaToneColors(resultMeta.tone, isDark).text,
          }}>{resultMeta.icon ? `${resultMeta.icon} ` : ''}{resultMeta.label}</span>
          {abhaResult?.fullName && (
            <p style={{ margin: '5px 0 0', fontSize: 13, fontWeight: 600, color: isDark ? '#e2e8f0' : '#111827' }}>
              {abhaResult.fullName}
            </p>
          )}
          {!linkedShown && abhaResult?.abhaNumber && (
            <p style={{ margin: '3px 0 0', fontSize: 12, color: isDark ? '#94a3b8' : '#374151' }}>
              <span style={{ color: isDark ? '#64748b' : '#9ca3af' }}>{ABHA_NUMBER_LABEL} </span>
              <span style={{ fontFamily: 'monospace', letterSpacing: '0.02em' }}>{maskAbhaNumber(abhaResult.abhaNumber)}</span>
            </p>
          )}
          {!linkedShown && abhaResult?.abhaAddress && (
            <p style={{ margin: '2px 0 0', fontSize: 12, color: isDark ? '#94a3b8' : '#374151' }}>
              <span style={{ color: isDark ? '#64748b' : '#9ca3af' }}>{ABHA_ADDRESS_LABEL} </span>
              <span style={{ fontFamily: 'monospace' }}>{maskAbhaAddress(abhaResult.abhaAddress)}</span>
            </p>
          )}
          <p style={{ margin: '4px 0 0', fontSize: 11, color: isDark ? '#64748b' : '#9ca3af' }}>
            {abhaResult?.source === 'scan_share' ? ABHA_SECTION.sourceScanShare
              : abhaResult?.source === 'verification' ? ABHA_SECTION.sourceVerify
              : abhaResult?.source === 'repository' ? ABHA_SECTION.sourceFile
              : ABHA_SECTION.sourceCreate}
          </p>
        </div>
        <button
          type="button"
          onClick={handleClear}
          style={{
            flexShrink: 0, fontSize: 12, fontWeight: 600, padding: '4px 10px',
            borderRadius: 6, cursor: 'pointer',
            border: `1px solid ${isDark ? '#334155' : '#cbd5e1'}`,
            background: isDark ? '#1e293b' : '#f8fafc',
            color: isDark ? '#94a3b8' : '#64748b',
          }}
        >
          Change
        </button>
      </div>
      <SendAbhaLinkSms phone={defaultMobile} />
      {patientId && <LinkedRecordsList patientId={patientId} isDark={isDark} onShowingChange={setLinkedShown} />}
    </div>
  );

  // ── Idle / flow body ──────────────────────────────────────────────────

  const optionBtn = (onClick, label, accent) => ({
    style: {
      flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 600,
      borderRadius: 7, cursor: 'pointer', textAlign: 'center',
      border: `1px solid ${accent ? (isDark ? 'rgba(20,184,166,0.4)' : '#99f6e4') : (isDark ? '#334155' : '#cbd5e1')}`,
      background: accent
        ? (isDark ? 'rgba(20,184,166,0.12)' : '#f0fdfa')
        : (isDark ? '#1e293b' : '#f8fafc'),
      color: accent ? (isDark ? '#2dd4bf' : '#0f766e') : (isDark ? '#94a3b8' : '#64748b'),
    },
    onClick,
    type: 'button',
  });

  const qrToggleActive = showQR;

  const idleBody = (
    <div style={body}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {canCreate && (
          <button {...optionBtn(() => { setMode('create'); setShowQR(false); }, CREATE_ABHA, true)}>
            + {CREATE_ABHA}
          </button>
        )}
        {canVerify && (
          <button {...optionBtn(() => { setMode('verify'); setShowQR(false); }, VERIFY_ABHA, false)}>
            {VERIFY_ABHA}
          </button>
        )}
        {canScanShare && (
        <button
          type="button"
          onClick={() => setShowQR((v) => !v)}
          style={{
            flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 600,
            borderRadius: 7, cursor: 'pointer', textAlign: 'center',
            border: `1px solid ${qrToggleActive ? (isDark ? 'rgba(20,184,166,0.4)' : '#99f6e4') : (isDark ? '#334155' : '#cbd5e1')}`,
            background: qrToggleActive ? (isDark ? 'rgba(20,184,166,0.12)' : '#f0fdfa') : (isDark ? '#1e293b' : '#f8fafc'),
            color: qrToggleActive ? (isDark ? '#2dd4bf' : '#0f766e') : (isDark ? '#94a3b8' : '#64748b'),
          }}
        >
          Scan &amp; Share {qrToggleActive ? '▲' : '▼'}
        </button>
        )}
      </div>

      {showQR && canScanShare && (
        <ScanShareCounterCards />
      )}
      <SendAbhaLinkSms phone={defaultMobile} />
    </div>
  );

  const flowBody = (
    <div style={{ ...body, padding: '12px 14px 14px', gap: 0 }}>
      {mode === 'create' && (
        <ABHACreationFlow onComplete={handleCreationComplete} onSkip={handleSkip} onSwitchToVerify={canVerify ? () => setMode('verify') : undefined} patientName={patientName} defaultMobile={defaultMobile} />
      )}
      {mode === 'verify' && (
        <ABHAVerificationFlow
          onComplete={handleVerificationComplete}
          onSkip={handleSkip}
          defaultMobile={defaultMobile}
          requireAbhaNumber
          onCreateAbha={canCreate ? () => setMode('create') : undefined}
        />
      )}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────

  if (!canAny) return null;

  return (
    <div style={sectionWrap}>
      {/* Internal collapsible header. Hidden in modal hosting (hideHeader) where the host owns the
          title + close — avoids a duplicate header and the "Optional" label after a deliberate
          Link/Verify action. */}
      {!hideHeader && (
        <button style={headerBtn} type="button" onClick={() => setIsOpen((v) => !v)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#2dd4bf' : '#0f766e' }}>
              {ABHA_SECTION.header}
            </span>
            {recordDegraded ? (
              <span style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#fcd34d' : '#b45309' }}>
                ⚠ Details unavailable
              </span>
            ) : (abhaResult || abhaStatus === 'VERIFIED' || abhaStatus === 'LINKED') ? (
              // Verified/linked (in-session result OR from the patient record) → show the STATUS,
              // never "Optional" and never the raw address. Details live in the expanded body.
              (() => {
                const meta = resultMeta;
                return (
                  <span style={{ fontSize: 11, fontWeight: 700, color: abhaToneColors(meta.tone, isDark).text }}>
                    {meta.icon ? `${meta.icon} ` : ''}{meta.label}
                  </span>
                );
              })()
            ) : (
              <span style={{ fontSize: 11, color: isDark ? '#64748b' : '#9ca3af', fontWeight: 400 }}>
                Optional
              </span>
            )}
          </div>
          <span style={chevron}>▼</span>
        </button>
      )}

      {(isOpen || hideHeader) && (
        abhaResult ? completedBody
          : recordDegraded ? degradedBody
          : mode !== 'idle' ? flowBody
          : idleBody
      )}
    </div>
  );
}
